import React, { useEffect, useMemo, useState } from 'react'
import SettingsEmptyState from '../../components/settings/SettingsEmptyState';
import { useTranslation } from 'react-i18next'
import { IoAddCircleOutline } from 'react-icons/io5'
import { MwButton, MwSpinnerButton, MwToast } from '../../components/ui'
import DeleteConfirmationModal from '../../components/ui/DeleteConfirmationModal'
import ActionsDropdown from '../../components/common/ActionsDropdown'
import ActiveIndicator from '../../components/common/ActiveIndicator'
import { useCurrent, useDelete, useFetch, usePost, usePut } from '../../hooks'

const toBool = (value) => value === true || value === 'true' || value === 1 || value === '1'

const PriceListModal = ({ isOpen, onClose, mode, initialValue, onSuccess }) => {
  const { t } = useTranslation('global')
  const { currentTenantId, currentCompanyId, currentBranchId } = useCurrent()
  const { postData, loading: postLoading, error: postError, data: postRes } = usePost(true)
  const { putData, loading: putLoading, error: putError, data: putRes } = usePut()

  const isEdit = mode === 'edit'
  const isAdd = mode === 'add'
  const isLoading = postLoading || putLoading

  const [name, setName] = useState(initialValue?.name ?? '')
  const [isActive, setIsActive] = useState(toBool(initialValue?.is_active))
  const [toastMessage, setToastMessage] = useState('')
  const [showToast, setShowToast] = useState(false)

  useEffect(() => {
    setName(initialValue?.name ?? '')
    setIsActive(toBool(initialValue?.is_active))
  }, [initialValue])

  useEffect(() => {
    if (postRes?.status === 1) {
      setToastMessage(t('priceList.saveSuccess'))
      setShowToast(true)
      onSuccess?.()
      onClose?.()
    }
  }, [postRes, onSuccess, onClose, t])

  useEffect(() => {
    if (putRes?.status === 1) {
      setToastMessage(t('priceList.updateSuccess'))
      setShowToast(true)
      onSuccess?.()
      onClose?.()
    }
  }, [putRes, onSuccess, onClose, t])

  useEffect(() => {
    if (postError || putError) {
      setToastMessage(t('common.errorSaving', 'حدث خطأ أثناء الحفظ. يرجى المحاولة مرة أخرى.'))
      setShowToast(true)
    }
  }, [postError, putError, t])

  const canSubmit = useMemo(() => !!name.trim(), [name])

  const submit = async () => {
    if (!canSubmit) return

    if (isAdd) {
      const payload = {
        tenant_id: currentTenantId,
        company_id: currentCompanyId,
        branch_id: currentBranchId,
        name: name.trim(),
        is_active: isActive ? 1 : 0
      }
      await postData(`${process.env.REACT_APP_INV_API_SERVER_BASE_URL}/inventory/list-price`, payload)
      return
    }

    await putData(`${process.env.REACT_APP_INV_API_SERVER_BASE_URL}/inventory/list-price/${initialValue?.id}`, {
      name: name.trim(),
      is_active: isActive ? 1 : 0
    })
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 overflow-auto bg-gray-800 bg-opacity-50 flex justify-center items-center text-xs">
      <MwToast msg={toastMessage} isShow={showToast} setIsShow={setShowToast} />
      <div className="bg-white w-full max-w-md mx-auto rounded-lg shadow-lg p-6 relative">
        {isLoading && (
          <div className="absolute inset-0 bg-white bg-opacity-80 flex flex-col justify-center items-center rounded-lg z-10">
            <div className="w-12 h-12 rounded-full border-4 border-blue-100 border-t-blue-500 animate-spin"></div>
          </div>
        )}

        <div className="flex justify-between items-center mb-4">
          <h2 className="text-gray-500 text-base">
            {isAdd ? t('priceList.addNew') : t('priceList.edit')}
          </h2>
          <button
            onClick={onClose}
            type="button"
            className="text-gray-500 hover:text-gray-700"
            disabled={isLoading}
          >
            ✕
          </button>
        </div>

        <div className="mb-4">
          <label className="block text-gray-500 text-sm mb-2">
            {t('priceList.name')}
          </label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full p-2 border rounded bg-white"
            disabled={isLoading}
          />
        </div>

        <div className="mb-4">
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
              className="mr-2"
              disabled={isLoading}
            />
            <span className="text-gray-500 text-sm">{t('common.active', 'نشط')}</span>
          </label>
        </div>

        <div className="flex justify-end gap-2">
          <MwButton type="cancelBtn" size="sm" onClick={onClose} disabled={isLoading}>
            {t('common.cancel')}
          </MwButton>
          <MwButton
            type="bottomBtn"
            size="sm"
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50"
            onClick={submit}
            disabled={!canSubmit || isLoading}
          >
            {isAdd ? t('common.add', 'إضافة') : t('common.save')}
          </MwButton>
        </div>
      </div>
    </div>
  )
}

const PriceListSettings = () => {
  const { t, i18n } = useTranslation('global')
  const isRTL = i18n.language === 'ar'
  const { currentTenantId, currentCompanyId, currentBranchId } = useCurrent()

  const listUrl =
    currentTenantId && currentCompanyId && currentBranchId
      ? `${process.env.REACT_APP_INV_API_SERVER_BASE_URL}/inventory/list-price/${currentTenantId}/${currentCompanyId}/${currentBranchId}`
      : null

  const { data, loading, refreshHandler } = useFetch(listUrl, !!listUrl)
  const { deleteItem, loading: deleteLoading } = useDelete()

  const lists = Array.isArray(data?.data) ? data.data : []

  const [modalOpen, setModalOpen] = useState(false)
  const [modalMode, setModalMode] = useState('add')
  const [selected, setSelected] = useState(null)
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const [toastMessage, setToastMessage] = useState('')
  const [showToast, setShowToast] = useState(false)

  const openAdd = () => {
    setSelected({ id: null, name: '', is_active: 1 })
    setModalMode('add')
    setModalOpen(true)
  }

  const openEdit = (list) => {
    setSelected(list)
    setModalMode('edit')
    setModalOpen(true)
  }

  const openDelete = (list) => {
    setSelected(list)
    setDeleteModalOpen(true)
  }

  const confirmDelete = async () => {
    try {
      await deleteItem(`${process.env.REACT_APP_INV_API_SERVER_BASE_URL}/inventory/list-price/${selected?.id}`)
      setToastMessage(t('priceList.deleteSuccess'))
      setShowToast(true)
      refreshHandler()
    } catch (e) {
      setToastMessage(t('common.errorSaving', 'حدث خطأ أثناء الحفظ. يرجى المحاولة مرة أخرى.'))
      setShowToast(true)
    } finally {
      setDeleteModalOpen(false)
      setSelected(null)
    }
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <MwSpinnerButton withLabel={false} />
      </div>
    )
  }

  if (!lists.length) {
    return (
      <div className="bg-gray-50 p-6 rounded-lg text-center">
        <SettingsEmptyState msg={t('priceList.noData')} />
        <div className="mt-3 flex justify-center">
          <MwButton inGroup={true} type="saveBtn" onClick={openAdd}>
            <IoAddCircleOutline />
            <span>{t('priceList.add')}</span>
          </MwButton>
        </div>
      </div>
    )
  }

  return (
    <div className="text-gray-500">
      <MwToast msg={toastMessage} isShow={showToast} setIsShow={setShowToast} />

      <div className="flex justify-between items-center mb-4">
        <p className="font-bold text-sm">{t('priceList.title')}</p>
        <MwButton
          onClick={openAdd}
          className="bg-blue-500 text-white px-4 py-2 rounded-md hover:bg-blue-600"
          type="saveBtn"
        >
          <IoAddCircleOutline size={16} />
          {t('priceList.add')}
        </MwButton>
      </div>

      <div className="w-full text-xs rounded-xl">
        <div className="flex gap-2 font-bold px-3 py-2 rounded-t-xl justify-between items-center">
          <div className="w-16 text-center">#</div>
          <div className="flex-1">{t('priceList.name')}</div>
          <div className="w-36 text-center">{t('priceList.productsCount')}</div>
          <div className="w-36 text-center">{t('priceList.createdAt')}</div>
          <div className="w-36 text-center">{t('priceList.status')}</div>
          <div className="w-36 text-center">{t('priceList.actions')}</div>
        </div>

        {lists.map((list, index) => (
          <div key={list.id} className="flex gap-2 px-3 py-1 border-t justify-between items-center text-xs">
            <div className="w-16 text-center">{index + 1}</div>
            <div className="flex-1">{list?.name || ''}</div>
            <div className="w-36 text-center">{Array.isArray(list?.items) ? list.items.length : ''}</div>
            <div className="w-36 text-center">
              {list?.created_at ? new Date(list.created_at).toLocaleDateString('en-EG') : ''}
            </div>
            <div className="w-36 text-center">
              <div className="w-36 flex justify-center items-center">
                <ActiveIndicator active={toBool(list?.is_active)} />
              </div>
            </div>
            <div className="w-36 text-center">
              <ActionsDropdown
                isRTL={isRTL}
                onEdit={() => openEdit(list)}
                onDelete={() => openDelete(list)}
                itemId={list.id}
                editText={t('common.edit')}
                deleteText={t('common.delete')}
              />
            </div>
          </div>
        ))}
      </div>

      {modalOpen && (
        <PriceListModal
          isOpen={modalOpen}
          onClose={() => setModalOpen(false)}
          mode={modalMode}
          initialValue={selected}
          onSuccess={refreshHandler}
        />
      )}

      {deleteModalOpen && (
        <DeleteConfirmationModal
          isOpen={deleteModalOpen}
          onClose={() => setDeleteModalOpen(false)}
          onConfirm={confirmDelete}
          title={t('priceList.deleteConfirmTitle')}
          message={t('priceList.deleteConfirmMessage')}
          loading={deleteLoading}
        />
      )}
    </div>
  )
}

export default PriceListSettings
